import { defineStore } from "pinia";
import { ref } from "vue";
import { i18n, persistLocale, type LocaleCode, detectLocale } from "@/plugins/i18n";
import { applyTheme, detectTheme, type ThemeMode } from "@/plugins/primevue";

const LS_SIDEBAR_COLLAPSED = "luckydesk.sidebar.collapsed";
const LS_INBOX_DETAILS_COLLAPSED = "luckydesk.inbox.details.collapsed";

function detectLSFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

export const useUIStore = defineStore("ui", () => {
  const locale = ref<LocaleCode>(detectLocale());
  const theme = ref<ThemeMode>(detectTheme());
  const sidebarCollapsed = ref<boolean>(detectLSFlag(LS_SIDEBAR_COLLAPSED));
  // Painel de detalhes do inbox recolhido — lembrado entre sessões (igual ao menu).
  const inboxDetailsCollapsed = ref<boolean>(detectLSFlag(LS_INBOX_DETAILS_COLLAPSED));

  function setLocale(loc: LocaleCode) {
    locale.value = loc;
    i18n.global.locale.value = loc;
    // O locale do PrimeVue (DatePicker/menus) acompanha `locale` via watcher em
    // App.vue (usePrimeVue), que precisa de contexto de componente.
    persistLocale(loc);
    document.documentElement.lang = loc;
  }

  function toggleTheme() {
    theme.value = theme.value === "dark" ? "light" : "dark";
    applyTheme(theme.value);
  }

  function toggleSidebar() {
    sidebarCollapsed.value = !sidebarCollapsed.value;
    try {
      localStorage.setItem(LS_SIDEBAR_COLLAPSED, sidebarCollapsed.value ? "1" : "0");
    } catch {
      /* private mode / quota — não-fatal, estado vive na sessão */
    }
  }

  function toggleInboxDetails() {
    inboxDetailsCollapsed.value = !inboxDetailsCollapsed.value;
    try {
      localStorage.setItem(LS_INBOX_DETAILS_COLLAPSED, inboxDetailsCollapsed.value ? "1" : "0");
    } catch {
      /* private mode / quota — não-fatal, estado vive na sessão */
    }
  }

  return {
    locale,
    theme,
    sidebarCollapsed,
    inboxDetailsCollapsed,
    setLocale,
    toggleTheme,
    toggleSidebar,
    toggleInboxDetails,
  };
});
